import React, { useState, useMemo } from "react";
import {
  IconPlus,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryGrayButton,
  Severity,
  TertiaryGrayButton
} from "@velaro/velaro-component-lib";
import { ConditionBlock } from "./ConditionBlock";
import { Expression, ExpressionType, SerializedCondition } from "./types";
import { useConditionPresets } from "@/providers/ConditionPresetsProvider";
import useToastAlert from "@/hooks/useToastAlert";

interface Props {
  expressionType: ExpressionType;
  expression: Expression;
  onUpdate(expression: Expression): void;
  /**
   * Hide the preset load/save controls. Used on pages that manage
   * presets themselves (e.g. the Condition Presets admin page) to
   * avoid recursive UI.
   */
  hidePresetControls?: boolean;
}
export function ExpressionEditor({
  expressionType,
  expression,
  onUpdate,
  hidePresetControls
}: Props) {
  const { presets, create } = useConditionPresets();
  const toast = useToastAlert();

  // ── Load-from-preset state ──────────────────────────────────────
  const [pendingPresetId, setPendingPresetId] = useState<number | "">("");
  const [loadMode, setLoadMode] = useState<"append" | "replace">("append");

  // ── Save-as-preset state ────────────────────────────────────────
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveDescription, setSaveDescription] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function addOrCondition() {
    const clone = expression ? [...expression] : [];
    clone.push([
      {
        type: "",
        data: "{}"
      }
    ]);
    onUpdate(clone);
  }

  function addAndCondition(i: number) {
    const andBlock = expression[i];
    andBlock.push({
      type: "",
      data: "{}"
    });
    onUpdate([...expression]);
  }

  function updateCondition(
    condition: SerializedCondition,
    i: number,
    j: number
  ) {
    const clone = expression ? [...expression] : [];
    clone[i][j] = condition;
    onUpdate(clone);
  }

  function removeCondition(i: number, j: number) {
    const clone = [...expression];
    const andBlock = clone[i];
    andBlock.splice(j, 1);
    if (andBlock.length === 0) {
      clone.splice(i, 1);
    }
    onUpdate(clone);
  }

  function renderAndBlock(conditions: SerializedCondition[], i: number) {
    return (
      <React.Fragment key={i}>
        <div className="rounded-lg bg-slate-gray-100 p-4 flex flex-col">
          <>{conditions.map((c, j) => renderCondition(c, i, j))}</>
          <TertiaryGrayButton
            label="AND — add condition to this group"
            prefixIcon={<IconPlus />}
            onClick={() => addAndCondition(i)}
            className="w-full"
          />
        </div>
        <Divider text="OR" />
      </React.Fragment>
    );
  }

  function renderCondition(
    condition: SerializedCondition,
    i: number,
    j: number
  ) {
    return (
      <React.Fragment key={j}>
        <ConditionBlock
          expressionType={expressionType}
          condition={condition}
          onUpdate={(c) => updateCondition(c, i, j)}
          onRemove={() => removeCondition(i, j)}
        />
        <Divider text="AND" />
      </React.Fragment>
    );
  }

  const hasConditions = expression && expression.length > 0;

  // Save-as-preset is disabled unless every condition has a type set.
  const hasEmptyConditionType = useMemo(() => {
    if (!hasConditions) return true;
    return expression.some((group) => group.some((c) => !c.type));
  }, [expression, hasConditions]);

  const saveDisabledReason = !hasConditions
    ? "Add at least one condition before saving as a preset."
    : hasEmptyConditionType
    ? "Finish configuring each condition before saving."
    : null;

  const existingNames = useMemo(
    () =>
      new Set(presets.map((p) => (p.name || "").trim().toLowerCase())),
    [presets]
  );

  function handleApplyPreset() {
    if (!pendingPresetId) return;
    const preset = presets.find((p) => p.id === pendingPresetId);
    if (!preset) return;

    let loaded: Expression;
    try {
      loaded = JSON.parse(preset.expressionJson) as Expression;
      if (!Array.isArray(loaded)) throw new Error("Invalid shape");
    } catch (e) {
      toast.displayToast?.(
        Severity.Error,
        `Preset '${preset.name}' is malformed and cannot be loaded.`
      );
      return;
    }

    if (loadMode === "replace") {
      onUpdate(loaded);
    } else {
      // append merges OR groups — both sides already represent OR-ed AND groups
      const base = expression ?? [];
      onUpdate([...base, ...loaded]);
    }

    toast.displayToast?.(
      Severity.Success,
      `Preset '${preset.name}' inlined — edits won't affect the original.`
    );
    setPendingPresetId("");
    setLoadMode("append");
  }

  async function handleSavePreset() {
    setSaveError(null);
    const name = saveName.trim();
    if (!name) {
      setSaveError("Name is required.");
      return;
    }
    if (existingNames.has(name.toLowerCase())) {
      setSaveError(
        `A preset named '${name}' already exists. Choose a different name.`
      );
      return;
    }
    setSaving(true);
    try {
      const result = await create({
        name,
        description: saveDescription.trim() || undefined,
        expressionJson: JSON.stringify(expression ?? [])
      });
      if (!result) {
        setSaveError(
          "Server rejected the preset (duplicate name or invalid condition)."
        );
        return;
      }
      toast.displayToast?.(
        Severity.Success,
        `Saved preset '${result.name}'.`
      );
      setShowSaveModal(false);
      setSaveName("");
      setSaveDescription("");
    } finally {
      setSaving(false);
    }
  }

  const showPresetControls = !hidePresetControls;

  return (
    <>
      {showPresetControls && (
        <div className="mb-3 flex flex-wrap items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-md">
          <label className="text-xs font-medium text-slate-600">
            Load from preset:
          </label>
          <select
            className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
            value={pendingPresetId}
            onChange={(e) =>
              setPendingPresetId(
                e.target.value ? parseInt(e.target.value, 10) : ""
              )
            }
            disabled={presets.length === 0}
          >
            <option value="">
              {presets.length === 0
                ? "No saved presets yet"
                : "— choose a preset —"}
            </option>
            {presets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {pendingPresetId && (
            <>
              <label className="text-xs text-slate-600 ml-2">
                <input
                  type="radio"
                  name="preset-load-mode"
                  checked={loadMode === "append"}
                  onChange={() => setLoadMode("append")}
                  className="mr-1"
                />
                Append
              </label>
              <label className="text-xs text-slate-600">
                <input
                  type="radio"
                  name="preset-load-mode"
                  checked={loadMode === "replace"}
                  onChange={() => setLoadMode("replace")}
                  className="mr-1"
                />
                Replace all
              </label>
              <TertiaryGrayButton
                label="Apply"
                onClick={handleApplyPreset}
              />
            </>
          )}
          <div className="grow" />
          <span title={saveDisabledReason ?? undefined}>
            <TertiaryGrayButton
              label="Save as preset"
              onClick={() => {
                setSaveName("");
                setSaveDescription("");
                setSaveError(null);
                setShowSaveModal(true);
              }}
              disabled={!!saveDisabledReason}
            />
          </span>
        </div>
      )}

      {hasConditions && (
        <>{expression.map(renderAndBlock)}</>
      )}
      <SecondaryGrayButton
        label={hasConditions ? "OR — add a new condition group" : "Add Condition"}
        prefixIcon={<IconPlus />}
        onClick={addOrCondition}
        className="w-full"
      />
      {hasConditions && (
        <p className="text-[11px] text-slate-400 mt-1 text-center">
          Conditions in the same group must ALL match (AND). Different groups are alternatives (OR).
        </p>
      )}

      {showPresetControls && (
        <Modal show={showSaveModal}>
          <ModalHeader
            title="Save as condition preset"
            onClose={() => setShowSaveModal(false)}
          />
          <ModalBody>
            <div className="m-4 flex flex-col gap-3">
              <p className="text-xs text-slate-500">
                Presets are reusable condition templates. Loading one into a
                workflow inlines a copy — future edits to the preset do not
                retroactively change workflows that already used it.
              </p>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-700">
                  Name
                </span>
                <input
                  className="border border-slate-300 rounded px-2 py-1 text-sm"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  maxLength={200}
                  placeholder="e.g. VIP SMS after hours"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-700">
                  Description (optional)
                </span>
                <textarea
                  className="border border-slate-300 rounded px-2 py-1 text-sm"
                  value={saveDescription}
                  onChange={(e) => setSaveDescription(e.target.value)}
                  maxLength={1000}
                  rows={2}
                />
              </label>
              {saveError && (
                <div className="text-xs text-red-600">{saveError}</div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="mx-4 my-2 flex justify-between items-center gap-2">
              <SecondaryGrayButton
                label="Cancel"
                onClick={() => setShowSaveModal(false)}
              />
              <PrimaryBlueButton
                label={saving ? "Saving…" : "Save preset"}
                onClick={handleSavePreset}
                disabled={saving}
              />
            </div>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}

function Divider({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 my-2">
      <div className="grow border border-slate-300"></div>
      <div className={`text-center text-xs font-bold px-2 py-0.5 rounded ${text === "OR" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{text}</div>
      <div className="grow border border-slate-300"></div>
    </div>
  );
}
